import jsPDF from 'jspdf';
import { addRobotoFont, setRobotoFont, areFontsLoaded } from './pdfFonts';
import { formatUserDate, formatUserDateTime } from '@/utils/date-formatter';

// Reuse ExportOptions from existing service
export type { ExportOptions } from './TicketPDFExportService';
import type { ExportOptions } from './TicketPDFExportService';

/**
 * Extended ticket data for tickets-tab PDF export (mobile-matching layout)
 * This interface extends the base ticket data with additional fields needed
 * to match the mobile PDF layout exactly.
 */
export interface TicketExportDataTicketsTab {
  id: string;
  ticketNumber: string;
  title: string;
  status: string;
  priority: string;
  assignedContractorName?: string;
  assigneeName?: string;
  location?: string;
  description?: string;
  gewerk?: string;
  typ?: string;
  dueDate?: string;
  extensionDate?: string;
  costs?: string;
  progress?: number;
  planLocation?: {
    x: number;
    y: number;
    planId: string;
    zoom: number;
    planName?: string;
  };
  planName?: string;
  planId?: string;
  planImageData?: string; // Base64 image data of the plan section
  photos?: string[]; // Array of base64 image data
  history?: any[];
  messages?: any[];
  // Mobile-matching fields
  creatorName?: string;
  reporterName?: string;
  creatorProfilePhotoUrl?: string;
  projectLogoUrl?: string;
  projectName: string;
  projectCity?: string;
  currency?: string;
  updatedAt?: string;
  overtimeNeeded?: boolean;
  overtimeHours?: number;
  overtimeConfirmed?: boolean;
  weatherTemperature?: string;
  weatherConditions?: string;
  weatherWind?: string;
}

// Labels for PDF - same as mobile app
const PDF_LABELS: Record<string, Record<string, string>> = {
  planLevel: { en: 'Plan Level', de: 'Plan-Ebene', tr: 'Plan Seviyesi' },
  createdBy: { en: 'Created by', de: 'Erstellt von', tr: 'Oluşturan' },
  gewerk: { en: 'Trade', de: 'Gewerk', tr: 'İş Kolu' },
  location: { en: 'Location', de: 'Raum', tr: 'Konum' },
  costs: { en: 'Costs', de: 'Kosten', tr: 'Maliyet' },
  dueDate: { en: 'Due Date', de: 'Frist', tr: 'Son Tarih' },
  extensionDate: { en: 'Extension', de: 'Nachfrist', tr: 'Uzatma' },
  assignedTo: { en: 'Assigned to', de: 'Zugewiesen an', tr: 'Atanan' },
  status: { en: 'Status', de: 'Status', tr: 'Durum' },
  priority: { en: 'Priority', de: 'Priorität', tr: 'Öncelik' },
  type: { en: 'Type', de: 'Typ', tr: 'Tür' },
  overtime: { en: 'Overtime', de: 'Nachtrag', tr: 'Fazla Mesai' },
  projectCity: { en: 'Project City', de: 'Projekt Stadt', tr: 'Proje Şehri' },
  weather: { en: 'Weather', de: 'Wetter', tr: 'Hava' },
  updatedAt: { en: 'Updated at', de: 'Aktualisiert am', tr: 'Güncelleme' },
  description: { en: 'Description', de: 'Beschreibung', tr: 'Açıklama' },
  page: { en: 'Page', de: 'Seite', tr: 'Sayfa' },
  hours: { en: 'hours', de: 'Stunden', tr: 'saat' },
  approved: { en: 'approved', de: 'genehmigt', tr: 'onaylandı' },
  pending: { en: 'pending', de: 'ausstehend', tr: 'beklemede' },
  history: { en: 'History', de: 'Historie', tr: 'Geçmiş' },
  messages: { en: 'Messages', de: 'Nachrichten', tr: 'Mesajlar' },
};

/**
 * Get localized label - matches mobile _getLabel function
 */
function getLabel(key: string, locale: string): string {
  const labelMap = PDF_LABELS[key];
  if (!labelMap) return key;
  return labelMap[locale] || labelMap['en'] || key;
}

/**
 * PDF Export Service for Tickets Tab - Mobile-Matching Layout
 * 
 * This service implements the EXACT same PDF layout as the mobile app
 * (ticket_pdf_service.dart). It is used ONLY by the tickets tab export.
 * The existing TicketPDFExportService.ts remains unchanged and is used
 * by the edit modal and other existing callers.
 */
export class TicketPDFExportServiceTicketsTab {
  private pdf: jsPDF;
  private locale: string;
  private dateFormat: string;
  private timeZone: string;
  
  // Colors matching mobile app
  private readonly primaryColor = { r: 74, g: 144, b: 226 }; // #4A90E2
  private readonly textColor = { r: 51, g: 51, b: 51 }; // #333333
  private readonly lightGray = { r: 245, g: 245, b: 245 }; // #F5F5F5
  private readonly borderGray = { r: 200, g: 200, b: 200 }; // #C8C8C8
  
  // Layout constants (in mm for jsPDF) - converted from mobile points (1pt = 0.353mm)
  private readonly marginLeft = 7; // 20pt = 7.06mm
  private readonly marginRight = 7; // 20pt = 7.06mm
  private readonly marginTop = 7; // 20pt = 7.06mm
  private readonly marginBottom = 18; // 50pt = 17.6mm (space for footer)
  private readonly contentWidth = 196; // A4 width (210) - margins
  private readonly pageHeight = 297; // A4 height in mm
  private readonly logoSize = 18; // 50pt = 17.6mm
  
  // Field layout
  private readonly labelWidth = 35; // 100pt = 35.3mm
  private readonly valueWidth = 56; // 160pt = 56.4mm
  private readonly columnGap = 7; // 20pt = 7.06mm
  private readonly rowHeight = 6.7; // (16pt + 3pt spacing) = 19pt = 6.7mm
  
  // Image dimensions (converted from mobile 265x180pt)
  private readonly sideImageWidth = 93.5; // 265pt = 93.5mm
  private readonly sideImageHeight = 63.5; // 180pt = 63.5mm
  private readonly imageGap = 5.3; // 15pt = 5.3mm

  constructor(
    locale: string = 'en',
    options?: { dateFormat?: string; timeZone?: string },
  ) {
    this.pdf = new jsPDF();
    this.locale = locale;
    this.dateFormat = options?.dateFormat || 'dd.MM.yyyy';
    this.timeZone = options?.timeZone || 'Europe/Berlin';
  }

  /**
   * Export multiple tickets to a single PDF file
   */
  async exportTickets(
    tickets: TicketExportDataTicketsTab[],
    projectName: string,
    options?: ExportOptions
  ): Promise<void> {
    // Load Roboto font for UTF-8/Turkish character support
    await addRobotoFont(this.pdf);
    if (tickets.length === 0) {
      throw new Error('No tickets selected for export');
    }

    const exportOptions: ExportOptions = options || {
      includePhotos: true,
      includeHistory: true,
      includeMessages: true,
    };

    // Track total pages for footer
    const ticketPageCounts: number[] = [];
    
    // First pass: calculate pages per ticket
    tickets.forEach((ticket) => {
      let pages = 1; // First page always
      
      // Count additional image pages
      if (exportOptions.includePhotos && ticket.photos && ticket.photos.length > 1) {
        const remainingImages = ticket.photos.length - 1;
        const imagesPerPage = 4; // 2 rows of 2 images per page after first
        pages += Math.ceil(remainingImages / imagesPerPage);
      }
      
      ticketPageCounts.push(pages);
    });

    let globalPageIndex = 0;
    const totalGlobalPages = ticketPageCounts.reduce((a, b) => a + b, 0);

    // Second pass: render each ticket
    tickets.forEach((ticket, ticketIndex) => {
      if (ticketIndex > 0) {
        this.pdf.addPage();
      }
      
      const pagesForThisTicket = ticketPageCounts[ticketIndex];
      this.renderTicket(ticket, exportOptions, globalPageIndex + 1, totalGlobalPages, pagesForThisTicket);
      globalPageIndex += pagesForThisTicket;
    });

    // Generate filename
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `tickets_export_${timestamp}.pdf`;
    
    this.pdf.save(filename);
  }

  /**
   * Render a single ticket (may span multiple pages)
   */
  private renderTicket(
    ticket: TicketExportDataTicketsTab,
    options: ExportOptions,
    startPageNum: number,
    totalPages: number,
    pagesForTicket: number
  ): void {
    let currentY = this.marginTop;
    const projectName = ticket.projectName;
    
    // ===== HEADER =====
    currentY = this.drawHeader(projectName, ticket.projectLogoUrl, ticket.creatorProfilePhotoUrl, currentY);
    
    // ===== TICKET TITLE =====
    this.pdf.setFontSize(14);
    setRobotoFont(this.pdf, 'bold');
    this.pdf.setTextColor(this.textColor.r, this.textColor.g, this.textColor.b);
    this.pdf.text(ticket.title || '', this.marginLeft, currentY);
    currentY += 10.6; // 30pt = 10.6mm
    
    // ===== FIELD ROWS (two-column layout) =====
    const fieldRows = this.buildFieldRows(ticket);
    currentY = this.drawFieldRows(fieldRows, currentY);
    currentY += 3.5; // 10pt = 3.5mm (gap before images)
    
    // ===== PLAN PREVIEW + FIRST IMAGE (side by side) =====
    currentY = this.drawSideBySideImages(
      ticket.planImageData,
      ticket.photos && ticket.photos.length > 0 ? ticket.photos[0] : undefined,
      currentY
    );
    currentY += 5.3; // 15pt = 5.3mm
    
    // ===== DESCRIPTION SECTION =====
    currentY = this.drawDescriptionSection(ticket.description || '', currentY);
    
    // ===== FOOTER on first page =====
    this.drawFooter(ticket.ticketNumber, startPageNum, totalPages);
    
    // ===== ADDITIONAL PAGES FOR REMAINING IMAGES =====
    if (options.includePhotos && ticket.photos && ticket.photos.length > 1) {
      let currentImageIndex = 1;
      let currentPageNum = startPageNum + 1;
      
      while (currentImageIndex < ticket.photos.length) {
        this.pdf.addPage();
        currentY = this.marginTop;
        
        // Header on subsequent pages
        currentY = this.drawHeader(projectName, ticket.projectLogoUrl, ticket.creatorProfilePhotoUrl, currentY);
        
        // Abbreviated field rows
        currentY = this.drawFieldRows(fieldRows, currentY);
        currentY += 3.5; // 10pt = 3.5mm (gap before images)
        
        // Draw images in rows of 2
        const imagesOnThisPage = Math.min(4, ticket.photos.length - currentImageIndex);
        for (let row = 0; row < 2 && currentImageIndex < ticket.photos.length; row++) {
          for (let col = 0; col < 2 && currentImageIndex < ticket.photos.length; col++) {
            const x = this.marginLeft + col * (this.sideImageWidth + this.imageGap);
            this.drawImage(ticket.photos[currentImageIndex], x, currentY, this.sideImageWidth, this.sideImageHeight);
            currentImageIndex++;
          }
          currentY += this.sideImageHeight + this.imageGap;
        }
        
        this.drawFooter(ticket.ticketNumber, currentPageNum, totalPages);
        currentPageNum++;
      }
    }
    
    // ===== OPTIONAL: HISTORY SECTION =====
    if (options.includeHistory && ticket.history && ticket.history.length > 0) {
      // Check if we need a new page
      if (currentY > this.pageHeight - 60) {
        this.pdf.addPage();
        currentY = this.marginTop;
      }
      currentY = this.drawHistorySection(ticket.history, currentY);
    }
    
    // ===== OPTIONAL: MESSAGES SECTION =====
    if (options.includeMessages && ticket.messages && ticket.messages.length > 0) {
      // Check if we need a new page
      if (currentY > this.pageHeight - 60) {
        this.pdf.addPage();
        currentY = this.marginTop;
      }
      currentY = this.drawMessagesSection(ticket.messages, currentY);
    }
  }

  /**
   * Draw header with circular logos and project name
   */
  private drawHeader(
    projectName: string,
    projectLogoUrl?: string,
    creatorPhotoUrl?: string,
    startY: number = this.marginTop
  ): number {
    const y = startY;
    const pageWidth = 210; // A4 width in mm
    
    // Left logo (project logo or placeholder)
    this.drawCircularImage(projectLogoUrl, this.marginLeft, y, this.logoSize);
    
    // Right logo (creator photo or placeholder)
    this.drawCircularImage(creatorPhotoUrl, pageWidth - this.marginRight - this.logoSize, y, this.logoSize);
    
    // Project name centered (mobile: textStartX = marginLeft + logoSize + 10)
    const textStartX = this.marginLeft + this.logoSize + 3.5; // 10pt = 3.5mm
    const textWidth = pageWidth - 2 * this.marginRight - 2 * this.logoSize - 7; // Account for gaps
    
    this.pdf.setFontSize(12);
    setRobotoFont(this.pdf, 'bold');
    this.pdf.setTextColor(this.primaryColor.r, this.primaryColor.g, this.primaryColor.b);
    
    // Mobile: logoAreaTop + (logoSize / 2) - 8, where 8pt ≈ 2.8mm
    const textY = y + this.logoSize / 2 - 2.8;
    this.pdf.text(projectName, textStartX + textWidth / 2, textY, { align: 'center' });
    
    // Horizontal line under header (10pt gap = 3.5mm)
    const lineY = y + this.logoSize + 3.5;
    this.pdf.setDrawColor(this.primaryColor.r, this.primaryColor.g, this.primaryColor.b);
    this.pdf.setLineWidth(0.3);
    this.pdf.line(this.marginLeft, lineY, pageWidth - this.marginRight, lineY);
    
    return lineY + 5.3; // 15pt = 5.3mm
  }

  /**
   * Draw a circular image or placeholder
   */
  private drawCircularImage(imageData: string | undefined, x: number, y: number, size: number): void {
    // Draw circle border
    this.pdf.setDrawColor(this.borderGray.r, this.borderGray.g, this.borderGray.b);
    this.pdf.setLineWidth(0.3);
    this.pdf.circle(x + size / 2, y + size / 2, size / 2, 'S');
    
    if (imageData && imageData.startsWith('data:image')) {
      try {
        // Detect format from data URL
        const format = imageData.includes('image/png') ? 'PNG' : 'JPEG';
        // jsPDF doesn't support circular clipping natively, so we draw the image in the circle area
        // The circle border provides the visual "frame"
        this.pdf.addImage(imageData, format, x + 1, y + 1, size - 2, size - 2);
      } catch (error) {
        console.error('[PDF Export] Failed to add circular image:', error);
        // Failed to add image, placeholder circle is already drawn
      }
    }
    // If no image, the circle border acts as placeholder
  }

  /**
   * Build field rows for the two-column layout
   */
  private buildFieldRows(ticket: TicketExportDataTicketsTab): Array<{ label: string; value: string }> {
    const formatDate = (dateStr?: string): string => {
      return formatUserDate(dateStr, this.dateFormat, this.timeZone);
    };
    
    const formatDateTime = (dateStr?: string): string => {
      return formatUserDateTime(dateStr, this.dateFormat, this.timeZone);
    };
    
    const buildOvertimeText = (): string => {
      if (!ticket.overtimeNeeded) return '-';
      const hours = ticket.overtimeHours || 0;
      const confirmed = ticket.overtimeConfirmed;
      const confirmText = confirmed ? getLabel('approved', this.locale) : getLabel('pending', this.locale);
      return `${hours} ${getLabel('hours', this.locale)}, ${confirmText}`;
    };
    
    const buildWeatherText = (): string => {
      const parts: string[] = [];
      if (ticket.weatherTemperature) parts.push(ticket.weatherTemperature);
      if (ticket.weatherConditions) parts.push(ticket.weatherConditions);
      if (ticket.weatherWind) parts.push(`Wind ${ticket.weatherWind}`);
      return parts.length > 0 ? parts.join(', ') : '-';
    };
    
    const getPlanLevelName = (): string => {
      if (ticket.planName) return ticket.planName;
      if (ticket.planLocation?.planName) return ticket.planLocation.planName;
      return '-';
    };
    
    return [
      { label: getLabel('planLevel', this.locale), value: getPlanLevelName() },
      { label: getLabel('createdBy', this.locale), value: ticket.creatorName || ticket.reporterName || '-' },
      { label: getLabel('gewerk', this.locale), value: ticket.gewerk || '-' },
      { label: getLabel('location', this.locale), value: ticket.location || '-' },
      { label: getLabel('costs', this.locale), value: ticket.costs ? `${ticket.currency || ''} ${ticket.costs}` : '-' },
      { label: getLabel('dueDate', this.locale), value: formatDate(ticket.dueDate) },
      { label: getLabel('extensionDate', this.locale), value: formatDate(ticket.extensionDate) },
      { label: getLabel('assignedTo', this.locale), value: ticket.assignedContractorName || ticket.assigneeName || '-' },
      { label: getLabel('status', this.locale), value: ticket.status || '-' },
      { label: getLabel('priority', this.locale), value: ticket.priority || '-' },
      { label: getLabel('type', this.locale), value: ticket.typ || '-' },
      { label: getLabel('overtime', this.locale), value: buildOvertimeText() },
      { label: getLabel('projectCity', this.locale), value: ticket.projectCity || '-' },
      { label: getLabel('weather', this.locale), value: buildWeatherText() },
      { label: getLabel('updatedAt', this.locale), value: formatDateTime(ticket.updatedAt) },
    ];
  }

  /**
   * Draw field rows in two-column layout
   */
  private drawFieldRows(
    fields: Array<{ label: string; value: string }>,
    startY: number
  ): number {
    let currentY = startY;
    
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(this.textColor.r, this.textColor.g, this.textColor.b);
    
    for (let i = 0; i < fields.length; i += 2) {
      // Left column
      const leftField = fields[i];
      setRobotoFont(this.pdf, 'bold');
      this.pdf.text(`${leftField.label}:`, this.marginLeft, currentY);
      setRobotoFont(this.pdf, 'normal');
      this.pdf.text(this.truncateText(leftField.value, this.valueWidth), this.marginLeft + this.labelWidth, currentY);
      
      // Right column (if exists)
      if (i + 1 < fields.length) {
        const rightField = fields[i + 1];
        const rightColumnStart = this.marginLeft + this.labelWidth + this.valueWidth + this.columnGap;
        setRobotoFont(this.pdf, 'bold');
        this.pdf.text(`${rightField.label}:`, rightColumnStart, currentY);
        setRobotoFont(this.pdf, 'normal');
        this.pdf.text(this.truncateText(rightField.value, this.valueWidth), rightColumnStart + this.labelWidth, currentY);
      }
      
      currentY += this.rowHeight;
    }
    
    return currentY;
  }

  /**
   * Truncate text to fit within a given width
   */
  private truncateText(text: string, maxWidth: number): string {
    if (!text) return '-';
    const width = this.pdf.getTextWidth(text);
    if (width <= maxWidth) return text;
    
    let truncated = text;
    while (this.pdf.getTextWidth(truncated + '...') > maxWidth && truncated.length > 0) {
      truncated = truncated.slice(0, -1);
    }
    return truncated + '...';
  }

  /**
   * Draw plan preview and first image side by side
   */
  private drawSideBySideImages(
    planImageData: string | undefined,
    firstPhoto: string | undefined,
    startY: number
  ): number {
    const leftX = this.marginLeft;
    const rightX = this.marginLeft + this.sideImageWidth + this.imageGap;
    
    console.log('[PDF Export] Drawing side-by-side images:', {
      hasPlanImage: !!planImageData,
      planImagePrefix: planImageData?.substring(0, 30),
      hasFirstPhoto: !!firstPhoto,
      firstPhotoPrefix: firstPhoto?.substring(0, 30),
    });
    
    // Left slot: Plan preview
    this.drawImage(planImageData, leftX, startY, this.sideImageWidth, this.sideImageHeight);
    
    // Right slot: First ticket photo
    this.drawImage(firstPhoto, rightX, startY, this.sideImageWidth, this.sideImageHeight);
    
    return startY + this.sideImageHeight;
  }

  /**
   * Draw an image or placeholder
   */
  private drawImage(
    imageData: string | undefined,
    x: number,
    y: number,
    width: number,
    height: number
  ): void {
    if (imageData && imageData.startsWith('data:image')) {
      try {
        const format = imageData.includes('image/png') ? 'PNG' : 'JPEG';
        this.pdf.addImage(imageData, format, x, y, width, height);
        return;
      } catch (error) {
        console.error('[PDF Export] Failed to add image:', error);
        // Failed to add image, draw placeholder
      }
    } else if (imageData) {
      console.warn('[PDF Export] Invalid image data format (not base64):', imageData?.substring(0, 50));
    }
    
    // Draw placeholder (border only)
    this.pdf.setDrawColor(this.borderGray.r, this.borderGray.g, this.borderGray.b);
    this.pdf.setLineWidth(0.3);
    this.pdf.rect(x, y, width, height, 'S');
  }

  /**
   * Draw description section with gray background
   */
  private drawDescriptionSection(description: string, startY: number): number {
    const descriptionHeight = 24.7; // 70pt = 24.7mm
    
    // Label
    this.pdf.setFontSize(10);
    setRobotoFont(this.pdf, 'bold');
    this.pdf.setTextColor(this.textColor.r, this.textColor.g, this.textColor.b);
    this.pdf.text(`${getLabel('description', this.locale)}:`, this.marginLeft, startY);
    startY += 6.3; // 18pt = 6.3mm
    
    // Gray background box
    this.pdf.setFillColor(this.lightGray.r, this.lightGray.g, this.lightGray.b);
    this.pdf.rect(this.marginLeft, startY, this.contentWidth, descriptionHeight, 'F');
    
    // Description text
    this.pdf.setFontSize(9);
    setRobotoFont(this.pdf, 'normal');
    const lines = this.pdf.splitTextToSize(description || '-', this.contentWidth - 6);
    this.pdf.text(lines.slice(0, 5), this.marginLeft + 3, startY + 5); // Limit to 5 lines
    
    return startY + descriptionHeight + 5.3; // 15pt = 5.3mm
  }

  /**
   * Draw footer on current page
   */
  private drawFooter(ticketNumber: string, pageNum: number, totalPages: number): void {
    const footerY = this.pageHeight - this.marginBottom + 8; // Position within bottom margin
    const pageWidth = 210;
    
    this.pdf.setFontSize(8);
    setRobotoFont(this.pdf, 'normal');
    this.pdf.setTextColor(this.textColor.r, this.textColor.g, this.textColor.b);
    
    // Left: Ticket ID
    this.pdf.text(`Ticket ID: ${ticketNumber}`, this.marginLeft, footerY);
    
    // Right: Page number
    const pageText = `${getLabel('page', this.locale)}: ${pageNum}/${totalPages}`;
    this.pdf.text(pageText, pageWidth - this.marginRight, footerY, { align: 'right' });
  }

  /**
   * Draw history section (optional, after description)
   */
  private drawHistorySection(history: any[], startY: number): number {
    this.pdf.setFontSize(12);
    setRobotoFont(this.pdf, 'bold');
    this.pdf.setTextColor(this.textColor.r, this.textColor.g, this.textColor.b);
    this.pdf.text(getLabel('history', this.locale), this.marginLeft, startY);
    startY += 6;
    
    this.pdf.setFontSize(9);
    
    for (const entry of history.slice(0, 10)) { // Limit to 10 entries
      if (startY > this.pageHeight - 20) {
        this.pdf.addPage();
        startY = this.marginTop;
      }
      
      const date = formatUserDateTime(entry.createdAt, this.dateFormat, this.timeZone);
      const user = entry.userName || 'Unknown';
      
      setRobotoFont(this.pdf, 'normal');
      this.pdf.text(`${date} - ${user}`, this.marginLeft, startY);
      startY += 4;
      
      setRobotoFont(this.pdf, 'bold');
      const description = entry.description || `${entry.changeType || ''}: ${entry.fieldName || ''}`;
      const lines = this.pdf.splitTextToSize(description, this.contentWidth);
      this.pdf.text(lines.slice(0, 2), this.marginLeft, startY);
      startY += lines.slice(0, 2).length * 4 + 3;
    }
    
    return startY;
  }

  /**
   * Draw messages section (optional, after history)
   */
  private drawMessagesSection(messages: any[], startY: number): number {
    this.pdf.setFontSize(12);
    setRobotoFont(this.pdf, 'bold');
    this.pdf.setTextColor(this.textColor.r, this.textColor.g, this.textColor.b);
    this.pdf.text(getLabel('messages', this.locale), this.marginLeft, startY);
    startY += 6;
    
    this.pdf.setFontSize(9);
    
    for (const message of messages.slice(0, 10)) { // Limit to 10 messages
      if (startY > this.pageHeight - 20) {
        this.pdf.addPage();
        startY = this.marginTop;
      }
      
      const date = formatUserDateTime(message.createdAt, this.dateFormat, this.timeZone);
      const user = message.userName || message.userCode || 'Unknown';
      
      setRobotoFont(this.pdf, 'normal');
      this.pdf.text(`${date} - ${user}`, this.marginLeft, startY);
      startY += 4;
      
      const lines = this.pdf.splitTextToSize(message.message || '', this.contentWidth);
      this.pdf.text(lines.slice(0, 3), this.marginLeft + 3, startY);
      startY += lines.slice(0, 3).length * 4 + 3;
    }
    
    return startY;
  }
}
